"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, CircleCheck as CheckCircle2, FileSearch, MessageSquare, ShieldCheck, Sparkles, UserCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const valuePoints = [{
  icon: ShieldCheck,
  title: "No long-term contracts",
  desc: "Work month-to-month with complete commercial flexibility."
}, {
  icon: UserCheck,
  title: "Direct fractional leadership",
  desc: "Gain senior strategic direction to align roadmaps, empower internal teams, and accelerate sustainable organic growth."
}, {
  icon: Sparkles,
  title: "Strategic leadership",
  desc: "Fractional director-level direction tailored to your market and growth stage."
}, {
  icon: CheckCircle2,
  title: "Revenue-focused SEO",
  desc: "Strategy aligned to qualified enquiries, pipeline value, and commercial intent."
}];
export default function Conversion() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.1,
        duration: 0.4
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="finalconversioncta" data-nav="light" className="relative bg-background py-20 lg:py-28 overflow-hidden text-foreground">
      {/* Decorative ambient background accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-secondary/30 rounded-full blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 right-0 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="max-w-4xl mx-auto text-center">
          {/* Eyebrow badge */}
          <motion.div variants={itemVariants} className="flex justify-center mb-5">
            <Badge variant="secondary" className="px-3.5 py-1 text-xs sm:text-sm font-medium tracking-wide uppercase border border-border">
              Direct consultant engagement
            </Badge>
          </motion.div>

          {/* Section heading */}
          <motion.h2 variants={itemVariants} className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground text-balance">
            Ready to scale your organic growth?
          </motion.h2>

          {/* Core pitch / first-person copy */}
          <motion.div variants={itemVariants} className="mt-6 space-y-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            <p>
              SEO should be a sustainable growth driver, not an opaque cost centre. With the right fractional SEO consultant, you get senior strategic leadership, clear priorities, and measurable commercial results without the overhead or commitment of a full-time in-house hire.
            </p>
            <p className="text-foreground/90 font-medium">
              I help growing UK businesses turn search into a structured revenue engine — whether you need a fractional head of SEO to guide strategy, hands-on execution to accelerate organic gains, or advisory for high-stakes technical migrations.
            </p>
          </motion.div>

          {/* Key engagement values / reassurance row */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10 text-left">
            {valuePoints.map((point, i) => {
            const IconComponent = point.icon;
            return <div key={point.title} data-index={i} className="rounded-lg border border-border bg-card p-4 transition-colors duration-200 hover:border-primary/40 shadow-xs">
                  <div className="flex items-center gap-2.5 text-primary mb-1.5 font-semibold text-sm">
                    <IconComponent className="h-4 w-4 shrink-0 text-primary" />
                    <span className="text-card-foreground">{point.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal">
                    {point.desc}
                  </p>
                </div>;
          })}
          </motion.div>

          {/* Interactive Dual CTA Cards */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
            {/* Primary Action Card */}
            <Card className="border-2 border-primary/20 bg-card shadow-md flex flex-col justify-between text-left relative overflow-hidden transition-colors hover:border-primary/50">
              <div aria-hidden="true" className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full pointer-events-none" />
              <CardContent className="p-6 sm:p-8 flex flex-col justify-between h-full gap-6">
                <div className="space-y-3">
                  <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-primary/10 text-primary text-xs font-semibold">
                    <SafeHydrate><Calendar className="h-3.5 w-3.5" /></SafeHydrate>
                    Direct strategy session
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl font-bold text-card-foreground">
                    Book a free SEO consultation
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {"Let's discuss how fractional SEO can solve your specific business problems and unlock your next stage of organic growth."}
                  </p>
                </div>

                <div className="pt-2">
                  <Button asChild size="lg" className="w-full justify-center text-sm font-semibold shadow-sm">
                    <Link to="/ContactPage" className="inline-flex items-center gap-2">
                      <span>Book a Free Consultation</span>
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Secondary Action Card */}
            <Card className="border border-border bg-card shadow-sm flex flex-col justify-between text-left transition-colors hover:border-accent/40">
              <CardContent className="p-6 sm:p-8 flex flex-col justify-between h-full gap-6">
                <div className="space-y-3">
                  <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-secondary text-secondary-foreground text-xs font-semibold">
                    <FileSearch className="h-3.5 w-3.5 text-primary" />
                    Baseline analysis
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl font-bold text-card-foreground">
                    Get a free SEO audit
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {"See exactly where your SEO stands today and what opportunities you're leaving on the table."}
                  </p>
                </div>

                <div className="pt-2">
                  <Button asChild variant="outline" size="lg" className="w-full justify-center text-sm font-semibold border-border hover:bg-secondary/50">
                    <Link to="/SeoAuditsPage" className="inline-flex items-center gap-2">
                      <span>Request an SEO Audit</span>
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Bottom assurance guarantee & subtle contextual links */}
          <motion.div variants={itemVariants} className="mt-10 pt-6 border-t border-border/80">
            <p className="text-sm sm:text-base font-medium text-foreground max-w-2xl mx-auto leading-relaxed">
              No long-term contracts. No management overhead. Just strategic SEO leadership focused on delivering measurable results for your business.
            </p>

            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs sm:text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                Want to know who you will work with?
                <Link to="/AboutMePage" className="font-medium text-primary hover:underline transition-colors">
                  Learn more about my background
                </Link>
              </span>
              <span className="hidden sm:inline text-border">•</span>
              <span className="flex items-center gap-1.5">
                Have an urgent question?
                <Link to="/ContactPage" className="font-medium text-primary hover:underline transition-colors inline-flex items-center gap-1">
                  <MessageSquare className="h-3.5 w-3.5" />
                  Get in touch directly
                </Link>
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}